// src/pages/Dashboard.jsx
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useModals } from '../hooks/useModals';
import { useNavigate } from 'react-router-dom';
import AccountBalanceCard from '../components/dashboard/AccountBalanceCard';
import BankAccountsCard from '../components/dashboard/BankAccountsCard';
import QuickActions from '../components/dashboard/QuickActions';
import useDashboardData from '../contexts/userDashboardData';
import { showToast } from '../utils/helpers';
import SendMoneyModal from '../components/modals/SendMoneyModal';
import RequestMoneyModal from '../components/modals/RequestMoneyModal';
import EscrowPayModal from '../components/modals/EscrowPayModal';
import BuyCreditModal from '../components/modals/BuyCreditModal';
import api from '../utils/api';
import { API_CONFIG } from '../utils/apiConfig';
import GenerateAccountCard from '../components/dashboard/GenerateAccountCard';
import SmartsuppChat from '../components/external/SmartsuppChat';
import AccountUnderReview from '../components/dashboard/AccountUnderReviewCard';
import GetNewAccountCard from '../components/dashboard/GetNewAccountCard';
import { ThemeContext } from '../components/context/themeContext';

const Wallet = () => {
  const { currentUser, currentAccountDetails, existingAccount, refreshUserData, logout } = useAuth();
  const { data, loading, error, refresh: refreshDashboardData } = useDashboardData();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasBankAccount, setHasBankAccount] = useState(false);
  const { modals, openModal, closeModal } = useModals();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useContext(ThemeContext);

  // Detect an existing connected bank account so the complete-profile card is
  // replaced by the real wallet content once the user has one.
  useEffect(() => {
    let mounted = true;
    api.get(API_CONFIG.USER.EXISTING_ACCOUNTS)
      .then((res) => {
        if (mounted && res.data?.success) {
          setHasBankAccount((res.data.data || []).length > 0);
        }
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  // Complete-profile flow (confirm business name → connect bank account)
  // finished — refresh so the wallet content shows instead of the card.
  const handleProfileCompleted = useCallback(async () => {
    setHasBankAccount(true);
    try {
      await refreshUserData();
    } catch (error) {
      console.error('Error refreshing after profile completion:', error);
    }
  }, [refreshUserData]);

  // ✅ Memoized refresh function
  const handlerefreshUserData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await refreshUserData();
      showToast('Data refreshed successfully', 'success');
    } catch (error) {
      console.error('Failed to refresh data:', error);
      showToast('Failed to refresh data', 'error');
    } finally {
      setIsRefreshing(false);
    }
  }, [refreshUserData]);

  // Handle account generation
  const handleGenerateAccount = async (formData) => {
    try {

      const submitData = new FormData();
      submitData.append("countryCode", formData.countryCode);
      submitData.append('phoneNumber', formData.phoneNumber);
      submitData.append('companyName', formData.companyName);
      submitData.append('userId', formData.userId);
      submitData.append('ownsBusiness', formData.ownsBusiness.toString());
      
      if (formData.ownsBusiness && formData.rc_number) {
        submitData.append('rc_number', formData.rc_number);
      }
      
      if (formData.ownsBusiness && formData.business_certificate) {
        submitData.append('business_certificate', formData.business_certificate);
      }

      // Payment account / virtual account creation is temporarily disabled.
      // const response = await api.post('/app/api/generate-account', submitData, {
      //   headers: {
      //     'Content-Type': 'multipart/form-data',
      //   },
      // });
      const response = { data: { success: false, message: 'Account generation is temporarily disabled' } };

      if (response.data.success) {
        // Handle different success scenarios based on response
        if (response.data.requires_verification) {
          // Business verification submitted case
          showToast(
            'Your business documents have been submitted successfully and are under review. Your account will be verified within 48 hours. You can update other information, but your account will remain unverified until manual verification is completed.',
            'success',
            8000 // Longer duration for important message
          );
          
          // Don't refresh user data or reload page for verification submissions
          // The account is not created yet, so we keep the modal open for user to see the message
          return { requiresVerification: true };
        } else {
          // Normal account creation case
          showToast('Account generated successfully!', 'success');
          await refreshUserData();
          setTimeout(() => {
            window.location.reload();
          }, 1000);
          return { requiresVerification: false };
        }
      } else {
        throw new Error(response.data.message || 'Failed to generate account');
      }
    } catch (error) {
      console.error('Account generation error:', error);
      
      // Enhanced error handling with specific messages
      let errorMessage = 'Failed to generate account';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showToast(errorMessage, 'error');
      throw error;
    }
  };

  // Creating NEw accounts 
    const handleNewWalletAccount = async (formData) => {
    try {

      const submitData = new FormData();
      submitData.append("countryCode", formData.countryCode);
      submitData.append('phoneNumber', formData.phoneNumber);
      submitData.append('companyName', formData.companyName);
      submitData.append('userId', formData.userId);
      submitData.append('ownsBusiness', formData.ownsBusiness.toString());
      
      if (formData.ownsBusiness && formData.rc_number) {
        submitData.append('rc_number', formData.rc_number);
      }
      
      if (formData.ownsBusiness && formData.business_certificate) {
        submitData.append('business_certificate', formData.business_certificate);
      }

      // Payment account / virtual account creation is temporarily disabled.
      // const response = await api.post('/app/api/generate-existing-account', submitData, {
      //   headers: {
      //     'Content-Type': 'multipart/form-data',
      //   },
      // });
      const response = { data: { success: false, message: 'Account generation is temporarily disabled' } };

      if (response.data.success) {
        // Handle different success scenarios based on response
        if (response.data.requires_verification) {
          // Business verification submitted case
          showToast(
            'Your business documents have been submitted successfully and are under review. Your account will be verified within 48 hours. You can update other information, but your account will remain unverified until manual verification is completed.',
            'success',
            8000 // Longer duration for important message
          );
          
          // Don't refresh user data or reload page for verification submissions
          // The account is not created yet, so we keep the modal open for user to see the message
          return { requiresVerification: true };
        } else {
          // Normal account creation case
          showToast('Account generated successfully!', 'success');
          await refreshUserData();
          setTimeout(() => {
            window.location.reload();
          }, 1000);
          return { requiresVerification: false };
        }
      } else {
        throw new Error(response.data.message || 'Failed to generate account');
      }
    } catch (error) {
      console.error('Account generation error:', error);
      
      // Enhanced error handling with specific messages
      let errorMessage = 'Failed to generate account';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showToast(errorMessage, 'error');
      throw error;
    }
  };


  // ✅ Handle authentication errors and redirect
  useEffect(() => {
    const isAuthError = error?.response?.status === 401 || error?.response?.status === 403;
    
    if (!loading && (isAuthError || (error && !data))) {
      console.log('Authentication error detected, logging out:', { error, data });
      showToast('Your session has expired. Please login again.', 'error');
      logout();
    }
  }, [loading, error, data, navigate, logout]);

  // Handle hash-based modal opening
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#send-money') openModal('sendMoney');
    if (hash === '#request-money') openModal('requestMoney');
    if (hash === '#escrow-pay') openModal('escrowPay');
    window.location.hash = '';
  }, []);

  // ✅ Fixed: Refresh data when modals close
  useEffect(() => {
    const handledModals = {
      sendMoney: modals.sendMoney,
      requestMoney: modals.requestMoney,
      escrowPay: modals.escrowPay
    };

    return () => {
      if (
        (handledModals.sendMoney && !modals.sendMoney) ||
        (handledModals.requestMoney && !modals.requestMoney) ||
        (handledModals.escrowPay && !modals.escrowPay)
      ) {
        setTimeout(() => {
          handlerefreshUserData();
        }, 1000);
      }
    };
  }, [modals.sendMoney, modals.requestMoney, modals.escrowPay, handlerefreshUserData]);

  // ✅ Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // ✅ If no user (should be handled by ProtectedRoute, but just in case)
  if (!currentUser) {
    return null;
  }

  return (
        <ThemeContext.Provider value={{ theme, toggleTheme }}>
              <div className={theme === 'dark' ? 'dark' : ''}>
    <div className="min-h-screen bg-white w-full max-w-5xl overflow-x-hidden pb-60">
      <div className="container mx-auto px-3 py-4">
        {/* Header with Refresh Button */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6 px-4">
          <div className="text-md sm:text-lg font-medium 900text-gray-">
            Hello, <span className="font-bold">{currentUser?.company_name || currentUser?.fullname}</span> 👋🏾
          </div>
          <button
            onClick={handlerefreshUserData}
            disabled={isRefreshing}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm w-full sm:w-auto justify-center"
          >
            {isRefreshing ? (
              <>
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Refreshing...
              </>
            ) : (
              <>
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                </svg>
                Refresh Data
              </>
            )}
          </button>
        </div>

        {/* ✅ Fixed conditional rendering */}
{!currentAccountDetails?.account_number && !hasBankAccount ? (
  // User doesn't have an account number
  currentUser?.name_change_status === "under-review" ? (
    // Account is under review
    <div className="mb-6">
      <AccountUnderReview user={currentUser} />
    </div>
  ) : (
    // No account and not under review - show complete profile flow
    <GenerateAccountCard 
      user={currentUser} 
      onCompleteProfile={handleProfileCompleted}
    />
  )
) : currentAccountDetails?.is_pre_paymeo > 0 ? (
  // Has account but needs to upgrade (pre_paymeo > 0)
  <GetNewAccountCard 
    OnCreateNewAccount={handleNewWalletAccount} 
    user={currentUser} 
  />
) : (
          <div className="space-y-6">
            <AccountBalanceCard
              user={currentUser}
              accountDetails={currentAccountDetails}
              existingAccount={existingAccount}
              dashboardData={data}
              onDataRefresh={handlerefreshUserData}
            />

            <BankAccountsCard />

            <QuickActions
              onSendMoney={() => openModal('sendMoney')}
              onRequestMoney={() => openModal('requestMoney')}
              onEscrowPay={() => openModal('escrowPay')}
              onAddProduct={() => navigate("/store#create")}
            />
            
          </div>
        )}

        {/* Modals */}
        <SendMoneyModal
          user={currentUser}
          show={modals.sendMoney}
          onHide={() => closeModal('sendMoney')}
          dashboardBalance={data?.account?.balance || 0}
          onSuccess={handlerefreshUserData}
        />
        <RequestMoneyModal
          show={modals.requestMoney}
          onHide={() => closeModal('requestMoney')}
          onSuccess={handlerefreshUserData}
        />
        <EscrowPayModal
          show={modals.escrowPay}
          onHide={() => closeModal('escrowPay')}
          onSuccess={handlerefreshUserData}
        />
        <BuyCreditModal
          show={modals.buyCredit}
          onHide={() => closeModal('buyCredit')}
          onSuccess={handlerefreshUserData}
        />
        <SmartsuppChat /> 
      </div>
    </div>
    </div>
    </ ThemeContext.Provider>
  );
};

export default Wallet;